import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, allCategoryId } from '@/mocks/categories';
import { comics } from '@/mocks/comics';

const sortOptions = [
  { id: 'latest', label: '最新更新', icon: 'ri-refresh-line' },
  { id: 'hot', label: '人气最热', icon: 'ri-fire-line' },
  { id: 'rating', label: '评分最高', icon: 'ri-star-line' },
  { id: 'complete', label: '完本佳作', icon: 'ri-check-double-line' },
];

const PAGE_SIZE = 12;

export default function Category() {
  const { categoryId = allCategoryId } = useParams<{ categoryId: string }>();
  const [searchParams] = useSearchParams();
  const sort = searchParams.get('sort') || 'latest';
  const q = searchParams.get('q') || '';
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const currentCategory = categories.find((c) => c.id === categoryId);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [categoryId, sort, q]);

  const list = useMemo(() => {
    let result = [...comics];
    if (categoryId !== allCategoryId) {
      result = result.filter((c) => c.categories.includes(categoryId));
    }
    if (sort === 'complete') {
      result = result.filter((c) => c.isCompleted);
    }
    const keyword = q.trim().toLowerCase();
    if (keyword) {
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(keyword) ||
          c.author.toLowerCase().includes(keyword) ||
          c.tags.some((t) => t.toLowerCase().includes(keyword)) ||
          c.description.toLowerCase().includes(keyword),
      );
    }
    if (sort === 'hot') {
      result = result.sort((a, b) => Number(b.views.replace('万', '')) - Number(a.views.replace('万', '')));
    } else if (sort === 'rating') {
      result = result.sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [categoryId, sort, q]);

  const visibleList = list.slice(0, visibleCount);
  const pageTitle = q
    ? `"${q}"的搜索结果-57漫画网`
    : currentCategory
      ? `${currentCategory.name}漫画免费阅读-57漫画网`
      : '全部分类漫画免费阅读-57漫画网';

  const jsonLd = [
    {
      '@type': 'CollectionPage',
      '@id': `https://readfunny0.github.io/category/${categoryId}#collectionpage`,
      url: `https://readfunny0.github.io/category/${categoryId}`,
      name: currentCategory ? `${currentCategory.name}漫画合集` : '全部分类漫画合集',
      description: currentCategory?.description ?? '57漫画网全部分类漫画，海量韩漫、热血、玄幻、恋爱、悬疑漫画免费阅读。',
      isPartOf: { '@id': 'https://readfunny0.github.io/#website' },
      inLanguage: 'zh-CN',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readfunny0.github.io/' },
        { '@type': 'ListItem', position: 2, name: '分类', item: 'https://readfunny0.github.io/category' },
        ...(currentCategory
          ? [{ '@type': 'ListItem', position: 3, name: currentCategory.name, item: `https://readfunny0.github.io/category/${categoryId}` }]
          : []),
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={
          currentCategory
            ? `${currentCategory.description}在57漫画网下拉式免费阅读，高清无删减，每日更新。`
            : '57漫画网全部分类漫画，韩漫、热血战斗、玄幻奇幻、都市生活、恋爱甜宠、悬疑推理漫画一网打尽，下拉式免费阅读。'
        }
        keywords={currentCategory?.keywords ?? '漫画,分类,免费漫画,57漫画网'}
        canonicalPath={`/category/${categoryId}`}
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '分类', to: '/category' },
                ...(currentCategory ? [{ label: currentCategory.name }] : []),
              ]}
            />
            <div className="mt-4 flex flex-col gap-2 md:mt-5">
              <h1 className="font-display text-2xl font-bold text-foreground-900 md:text-3xl">
                {q ? `搜索：${q}` : currentCategory ? `${currentCategory.name}漫画` : '全部漫画'}
              </h1>
              <p className="text-xs text-foreground-500 md:text-sm">
                {currentCategory?.description ?? '选择下方分类快速定位，海量漫画下拉式免费阅读，每日 150+ 话更新。'}
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="漫画分类切换">
              <Link
                to="/category"
                role="tab"
                aria-selected={categoryId === allCategoryId}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  categoryId === allCategoryId
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                <i className="ri-apps-2-line h-4 w-4" aria-hidden="true"></i>
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  role="tab"
                  aria-selected={categoryId === cat.id}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                    categoryId === cat.id
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} h-4 w-4`} aria-hidden="true"></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1" role="tablist" aria-label="排序方式">
              {sortOptions.map((opt) => {
                const active = sort === opt.id;
                return (
                  <Link
                    key={opt.id}
                    to={`/category/${categoryId}${opt.id === 'latest' ? '' : `?sort=${opt.id}`}`}
                    role="tab"
                    aria-selected={active}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors md:px-4 md:text-sm ${
                      active ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:bg-secondary-100 hover:text-primary-700'
                    }`}
                  >
                    <i className={`${opt.icon} h-4 w-4`} aria-hidden="true"></i>
                    {opt.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between text-xs text-foreground-500 md:mt-8 md:text-sm">
            <p>
              共 <strong className="text-primary-600">{list.length}</strong> 部作品
            </p>
            {sort === 'hot' && (
              <p className="flex items-center gap-1">
                <i className="ri-fire-fill h-4 w-4 text-accent-500" aria-hidden="true"></i>
                按全站人气排序
              </p>
            )}
          </div>

          {visibleList.length > 0 ? (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:mt-6 lg:grid-cols-5 md:gap-5">
              {visibleList.map((comic, index) => (
                <ComicCard key={comic.id} comic={comic} index={sort === 'hot' ? index : undefined} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
              <i className="ri-inbox-line h-12 w-12 text-foreground-400" aria-hidden="true"></i>
              <p className="mt-4 text-sm font-medium text-foreground-700 md:text-base">没有找到相关作品</p>
              <p className="mt-2 text-xs text-foreground-500">换个关键词或分类试试吧</p>
              <Link to="/category" className="mt-5 flex items-center gap-1 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-arrow-left-line h-4 w-4" aria-hidden="true"></i>
                返回全部分类
              </Link>
            </div>
          )}

          {visibleCount < list.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-8 py-2.5 text-sm font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-loader-4-line h-4 w-4" aria-hidden="true"></i>
                加载更多（已显示 {visibleList.length}/{list.length}）
              </button>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </>
  );
}